import { useState, useCallback, useEffect, createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LAGEN } from '@xxtract/ui';
import { cn } from '@/lib/utils';

export interface ToastTranslation {
  key: string;
  values?: Record<string, string | number | ToastTranslation>;
}

export type ToastMessage = string | ToastTranslation;

interface Toast {
  id: string;
  message: ToastMessage;
  type: 'success' | 'error' | 'info';
}

interface ToastContextType {
  addToast: (message: ToastMessage, type?: Toast['type']) => void;
}

const ToastContext = createContext<ToastContextType>({ addToast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  // The hook rerenders this mounted provider through react-i18next's application-wide
  // language subscription, the same path used by the app shell.
  const language = i18n.resolvedLanguage ?? i18n.language;

  const addToast = useCallback((message: ToastMessage, type: Toast['type'] = 'success') => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className={cn('fixed right-4 top-4 flex flex-col gap-2', LAGEN.melding)}>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} language={language} i18n={i18n} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function resolveToastMessage(message: ToastMessage, translate: (key: string, values?: Record<string, unknown>) => string): string {
  if (typeof message === 'string') return message;
  const values = Object.fromEntries(
    Object.entries(message.values ?? {}).map(([name, value]) => [
      name,
      typeof value === 'object' ? resolveToastMessage(value, translate) : value,
    ]),
  );
  return translate(message.key, values);
}

function ToastItem({
  toast,
  onDismiss,
  language,
  i18n,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
  language: string;
  i18n: ReturnType<typeof useTranslation>['i18n'];
}) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), 3000);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  const message = resolveToastMessage(toast.message, (key, values) => i18n.t(key, { ...values, lng: language }) as string);

  const bgColor =
    toast.type === 'success'
      ? 'var(--success)'
      : toast.type === 'error'
        ? 'var(--destructive)'
        : 'var(--primary)';

  return (
    <div
      data-testid="toast"
      className="flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg"
      style={{ backgroundColor: bgColor, minWidth: '200px' }}
      role="alert"
    >
      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
        {toast.type === 'success' ? 'check_circle' : toast.type === 'error' ? 'error' : 'info'}
      </span>
      {message}
      <button
        onClick={() => onDismiss(toast.id)}
        className="ml-auto opacity-70 hover:opacity-100"
        aria-label="Dismiss"
      >
        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
          close
        </span>
      </button>
    </div>
  );
}
